import { useChanged } from "../Shared/useChanged.js";
import React, { useState } from "react";
import { Briefcase, Camera, GraduationCap, KeyRound, LogOut, Mic, Settings2, User, Wallet } from "lucide-react";
import { apiPatch } from "../Api/Client.js";
import { runsCompany } from "../Services/pageAccess.js";
import { companyBrandName } from "../Shared/companyBrand.js";
import { formatMoney } from "../Shared/money.js";
import { latestPayProfile, resolveActiveEmployee } from "../Shared/ScreenUtils.jsx";
import VoiceRecorder from "../Components/VoiceRecorder.jsx";
import ChangeOwnPassword from "../Components/ChangeOwnPassword.jsx";
import { GettingStarted, ResignationSection } from "../Products/people/index.js";
import { Alert, Avatar, Button, Card, DetailList, Disclosure, Field, FileButton, FormGrid, Input, Page, PageHeader, Textarea } from "../Ui/index.jsx";
import "../Styles/ProfileScreen.css";

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;
const MAX_AVATAR_DIMENSION = 512;

// Resized and re-encoded client-side purely so a slow connection isn't
// stuck uploading a 12MB phone photo — the server re-does all of this
// itself (Apps/Users/views.py's "avatar" action) and is the real limit,
// not this.
function compressAvatarImage(file) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > height && width > MAX_AVATAR_DIMENSION) {
        height = Math.round((height * MAX_AVATAR_DIMENSION) / width);
        width = MAX_AVATAR_DIMENSION;
      } else if (height > MAX_AVATAR_DIMENSION) {
        width = Math.round((width * MAX_AVATAR_DIMENSION) / height);
        height = MAX_AVATAR_DIMENSION;
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Could not process that image."))),
        "image/jpeg",
        0.82
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read that file.")); };
    img.src = url;
  });
}


// One collapsible card. Sections someone looks at often (their own contact
// details, the facts about their role) open by default; set-once-at-
// onboarding or rarely-needed ones (education, pay, account/session
// technicalia) start collapsed, one click away instead of permanently
// taking up space on every visit.
function ProfileSection({ title, subtitle, icon, defaultOpen, children }) {
  return (
    <Disclosure card summary={title} subtitle={subtitle} icon={icon} defaultOpen={defaultOpen}>
      {children}
    </Disclosure>
  );
}

// A section whose fields the person edits and saves: an error line, the fields, and one Save button.
function EditableSection({ error, busy, changed, onSave, children, ...section }) {
  return (
    <ProfileSection {...section}>
      {error && <Alert tone="danger">{error}</Alert>}
      <FormGrid columns={2}>{children}</FormGrid>
      <div className="Profile-Save">
        <Button variant="primary" onClick={onSave} disabled={busy || !changed}>{busy ? "Saving…" : "Save changes"}</Button>
      </div>
    </ProfileSection>
  );
}

export function ProfileScreen({ data, onLogout, reload, navigate }) {
  const user = data.me?.user || data.me?.account || data.me || {};
  const employee = resolveActiveEmployee(data) || {};
  const fullName = employee.display_name || employee.displayName || user.fullName || user.full_name || user.username || "User";
  const payProfile = latestPayProfile(data.payProfiles, employee.id);

  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState("");

  const onAvatarChange = async (file) => {
    if (!file.type.startsWith("image/")) { setAvatarError("Pick an image file."); return; }
    if (file.size > MAX_AVATAR_BYTES) { setAvatarError("That file is over 5MB."); return; }
    setAvatarBusy(true);
    setAvatarError("");
    try {
      const compressed = await compressAvatarImage(file);
      const formData = new FormData();
      formData.append("avatar", compressed, "avatar.jpg");
      await apiPatch(`/Users/EmployeeProfiles/${employee.id}/avatar/`, formData);
      if (reload) reload(["employees", "me"]);
    } catch (err) {
      setAvatarError(err?.payload?.detail || err?.message || "Could not update your photo.");
    } finally {
      setAvatarBusy(false);
    }
  };

  // Contact & Personal — the fields someone actually comes here to change.
  const [contactForm, setContactForm] = useState({
    display_name: employee.display_name || "",
    contact_number: employee.contact_number || "",
    city: employee.city || "",
    address: employee.profile_payload?.address || "",
    emergency_contact: employee.profile_payload?.emergency_contact || "",
  });
  const [contactChanged, contactSaved] = useChanged(contactForm);
  const [contactBusy, setContactBusy] = useState(false);
  const [contactError, setContactError] = useState("");
  const saveContact = async () => {
    setContactBusy(true);
    setContactError("");
    try {
      await apiPatch(`/Users/EmployeeProfiles/${employee.id}/`, {
        display_name: contactForm.display_name,
        contact_number: contactForm.contact_number,
        city: contactForm.city,
      });
      // address/emergency_contact live inside the free-form profile_payload
      // blob, which the generic PATCH above deliberately never allows (it's
      // not a single field, it's a bag HR also writes to) — merged through
      // the dedicated patch-payload action instead.
      await apiPatch(`/Users/EmployeeProfiles/${employee.id}/patch-payload/`, {
        profile_payload: { address: contactForm.address, emergency_contact: contactForm.emergency_contact },
      });
      if (reload) reload(["employees", "me"]);
      contactSaved();
    } catch (err) {
      setContactError(err?.payload?.detail || err?.message || "Could not save those changes.");
    } finally {
      setContactBusy(false);
    }
  };

  // Integrations & Tools — GitHub/Slack/Calendar/weekly hours.
  const [toolsForm, setToolsForm] = useState({
    github_username: employee.github_username || "",
    slack_username: employee.slack_username || "",
    calendar_id: employee.calendar_id || "",
    availability_hours: employee.availability_hours || 40,
  });
  const [toolsChanged, toolsSaved] = useChanged(toolsForm);
  const [toolsBusy, setToolsBusy] = useState(false);
  const [toolsError, setToolsError] = useState("");
  const saveTools = async () => {
    setToolsBusy(true);
    setToolsError("");
    try {
      // People often paste their Slack profile LINK (.../team/U01AB2CDE3F); what is
      // stored is the member ID at the end of it.
      const slackMatch = String(toolsForm.slack_username || "").match(/slack\.com\/team\/([A-Z0-9]+)/i);
      await apiPatch(`/Users/EmployeeProfiles/${employee.id}/`, {
        github_username: toolsForm.github_username,
        slack_username: slackMatch ? slackMatch[1].toUpperCase() : toolsForm.slack_username,
        calendar_id: toolsForm.calendar_id,
        availability_hours: Number(toolsForm.availability_hours) || 40,
      });
      if (reload) reload(["employees", "me"]);
      toolsSaved();
    } catch (err) {
      setToolsError(err?.payload?.detail || err?.message || "Could not save those changes.");
    } finally {
      setToolsBusy(false);
    }
  };

  // Education — set once at onboarding, so its own small section rather
  // than mixed in with things people actually revisit.
  const [eduForm, setEduForm] = useState({
    college_name: employee.college_name || "",
    year_of_graduation: employee.year_of_graduation || "",
  });
  const [eduChanged, eduSaved] = useChanged(eduForm);
  const [eduBusy, setEduBusy] = useState(false);
  const [eduError, setEduError] = useState("");
  const saveEdu = async () => {
    setEduBusy(true);
    setEduError("");
    try {
      await apiPatch(`/Users/EmployeeProfiles/${employee.id}/`, {
        college_name: eduForm.college_name,
        year_of_graduation: Number(eduForm.year_of_graduation) || null,
      });
      if (reload) reload(["employees", "me"]);
      eduSaved();
    } catch (err) {
      setEduError(err?.payload?.detail || err?.message || "Could not save those changes.");
    } finally {
      setEduBusy(false);
    }
  };

  // "Getting started" (the same card as on Home) is re-read after each save and after a voice sample changes.
  const [setupTick, setSetupTick] = useState(0);
  const refreshSetup = () => setSetupTick((n) => n + 1);
  const setupKey = [setupTick, employee.contact_number, employee.github_username, employee.slack_username].join("|");

  const joinedLabel = employee.joined_on
    ? new Date(employee.joined_on).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "-";


  return (
    <Page className="Profile">
      <header className="Profile-Header">
        <div className="Profile-Photo">
          <Avatar size="xl" name={fullName} src={employee.avatar_url} />
          <FileButton size="sm" className="Profile-Photo__edit" title="Change photo" aria-label="Change photo" disabled={avatarBusy} accept="image/png, image/jpeg, image/webp" onFile={onAvatarChange}>
            <Camera size={14} />
          </FileButton>
        </div>
        <PageHeader
          title={fullName}
          subtitle={<>{employee.department_name || companyBrandName(data.me?.company)} &middot; {employee.position_title || "Intranet User"}</>}
          actions={<Button leftIcon={<LogOut size={14} />} onClick={onLogout}>Logout</Button>}
        />
      </header>
      {avatarError && <Alert tone="danger">{avatarError}</Alert>}

      <Card>
        <DetailList columns={4} items={[
          ["Department", employee.department_name || "-"],
          ["Employment Type", employee.employment_type || "-"],
          ["Manager", employee.manager_name || "-"],
          ["Joined", joinedLabel],
        ]} />
      </Card>

      <GettingStarted employee={employee} refreshKey={setupKey} />

      <EditableSection title="Contact & Personal" subtitle="Editable — reach you, find you" icon={<User size={16} />} defaultOpen error={contactError} busy={contactBusy} changed={contactChanged} onSave={saveContact}>
        <Field label="Display Name"><Input value={contactForm.display_name} onChange={(e) => setContactForm({ ...contactForm, display_name: e.target.value })} /></Field>
        <Field label="Contact Number"><Input value={contactForm.contact_number} onChange={(e) => setContactForm({ ...contactForm, contact_number: e.target.value })} /></Field>
        <DetailList columns={1} items={[["Personal Email", user.email || employee.email || "-"]]} />
        <Field label="City"><Input value={contactForm.city} onChange={(e) => setContactForm({ ...contactForm, city: e.target.value })} /></Field>
        <Field label="Address" className="is-wide"><Textarea rows={2} value={contactForm.address} onChange={(e) => setContactForm({ ...contactForm, address: e.target.value })} /></Field>
        <Field label="Emergency Contact" className="is-wide"><Input value={contactForm.emergency_contact} onChange={(e) => setContactForm({ ...contactForm, emergency_contact: e.target.value })} /></Field>
      </EditableSection>

      <ProfileSection title="Work" subtitle="Set by HR — not yours to edit here" icon={<Briefcase size={16} />} defaultOpen>
        <DetailList items={[
          ["Position", employee.position_title || "-"],
          ["Manager", employee.manager_name || "-"],
          ["Employment Type", employee.employment_type || "-"],
          ["Status", employee.status || "-"],
          ["Joined", joinedLabel],
          ["Employee Code", employee.employee_code || "-"],
        ]} />
      </ProfileSection>

      <EditableSection title="Integrations & Tools" subtitle="GitHub, Slack, Calendar, weekly hours" icon={<Settings2 size={16} />} error={toolsError} busy={toolsBusy} changed={toolsChanged} onSave={saveTools}>
        <Field label="GitHub Username" hint="Only needed if you are in product engineering and push code."><Input value={toolsForm.github_username} onChange={(e) => setToolsForm({ ...toolsForm, github_username: e.target.value })} /></Field>
        <Field label="Slack member ID" hint="In Slack: click your name, then the three dots, then Copy member ID. Pasting your profile link works too."><Input value={toolsForm.slack_username} onChange={(e) => setToolsForm({ ...toolsForm, slack_username: e.target.value })} placeholder="U01AB2CDE3F" /></Field>
        <Field label="Calendar ID"><Input value={toolsForm.calendar_id} onChange={(e) => setToolsForm({ ...toolsForm, calendar_id: e.target.value })} /></Field>
        <Field label="Availability (hrs/week)"><Input type="number" min="0" max="168" value={toolsForm.availability_hours} onChange={(e) => setToolsForm({ ...toolsForm, availability_hours: e.target.value })} /></Field>
      </EditableSection>

      <ProfileSection title="Voice sample" subtitle="Lets the meeting recorder know it is you speaking" icon={<Mic size={16} />}>
        <p className="ds-card__sub">
          The meeting recorder labels who said what. With a short sample of your voice it can credit your
          words to you; without one, your part of a recorded meeting shows up as an unknown speaker.
        </p>
        <VoiceRecorder employeeId={employee.id} employeeName={fullName} onChange={refreshSetup} />
      </ProfileSection>

      <EditableSection title="Education" subtitle="Filled in once at onboarding" icon={<GraduationCap size={16} />} error={eduError} busy={eduBusy} changed={eduChanged} onSave={saveEdu}>
        <Field label="College"><Input value={eduForm.college_name} onChange={(e) => setEduForm({ ...eduForm, college_name: e.target.value })} /></Field>
        <Field label="Year of Graduation"><Input type="number" min="2000" max="2030" value={eduForm.year_of_graduation} onChange={(e) => setEduForm({ ...eduForm, year_of_graduation: e.target.value })} /></Field>
      </EditableSection>

      <ProfileSection title="Pay" subtitle="Visible here, changed only through Finance" icon={<Wallet size={16} />}>
        <DetailList items={[
          ["Base Pay", <span className="ds-num">{payProfile?.base_pay ? formatMoney(payProfile.base_pay, data.me?.company) : "-"}</span>],
          ["Pay Type", payProfile?.pay_type || employee.profile_payload?.pay_type || "-"],
        ]} />
      </ProfileSection>

      <ProfileSection title="Account & Session" subtitle="Username, workspace, password" icon={<KeyRound size={16} />}>
        <DetailList items={[
          ["Username", user.username || employee.username || "-"],
          // Deliberately NOT the workspace login address (username@banao.tech).
          // That string is a sign-in credential shaped like an email, not a
          // mailbox — showing it here made everyone (people and AI alike)
          // treat it as one. Belonging to the organisation is the fact worth
          // showing, the word, not a fake address. (Decision: Saurabh,
          // 2026-08-05.) Company Email itself is never shown on this page at
          // all (2026-10-07) — most people don't have one, and anyone who
          // does already knows it from their connected calendar.
          ["Organisation", employee.account ? (data.me?.company?.brandName || data.me?.company?.displayName || "-") : "-"],
          ["Tenant", data.me?.activeTenant?.name || data.me?.tenant?.name || data.me?.tenant || "Default Tenant"],
          ["Workspace", data.me?.activeWorkspace?.name || data.me?.workspace?.name || data.me?.workspace || "Default Workspace"],
        ]} />
        <ChangeOwnPassword />
        {runsCompany(data.me) && navigate && (
          <p className="ds-small">
            Admin:{" "}
            <Button variant="link" onClick={() => navigate("/change-password/")}>reset another person's password</Button>
          </p>
        )}
      </ProfileSection>

      <ProfileSection title="Resignation" subtitle="Leaving the company — read the process first" icon={<LogOut size={16} />}>
        <ResignationSection navigate={navigate} />
      </ProfileSection>

      {/* The same quiet "Terms"-style link as the sidebar footer: small, not another action button. */}
      {navigate && (
        <p className="ds-small">
          <Button variant="link" onClick={() => navigate("/policies/")}>Policies</Button>
        </p>
      )}
    </Page>
  );
}
